/**
 * Calls tab (docs/plans/oncall-line-plan.md 5, 7.5, 7.7; WP-12; the default
 * tab, for the whole on-call team of the site, plan Q8).
 *
 *   the list      GET /calls for the site in the header (switching site
 *                 switches line), unreviewed calls first, filterable by status
 *                 and review state, 50 at a time with "Load more"
 *   the poll      the list (and the super_admin strip) asked again every 60 s
 *                 while the page is visible, polling, not SSE (plan 7.7)
 *   the drawer    /oncall/calls/:callId, the link every alert email carries
 *                 (OncallApp routes both /oncall and that path here), opens
 *                 CallDetailDrawer; closing it returns to /oncall
 *   All lines     AllLinesStrip, for super_admins only
 *
 * GET /calls carries no caller identity and writes no audit row, so the poll
 * is free to repeat it; the drawer's audited detail read is never polled.
 * Every time shown is the site's wall clock (useAuth().currentSite.timezone,
 * lib/format.ts). A slow answer for a site or a filter that is no longer
 * current is dropped.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle, Loader2, PhoneCall } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import PageHeader, { RefreshButton } from '../../ui/PageHeader';
import EmptyState from '../../ui/EmptyState';
import FilterChip from '../../ui/FilterChip';
import SegmentedControl from '../../ui/SegmentedControl';
import { secondaryBtnCls } from '../../ui/styles';
import type { OncallCallStatus, OncallCallSummary, OncallHealth, OncallReviewStatus } from '../api/oncallApi';
import { apiErrorMessage, getOncallHealth, listCalls } from '../api/oncallApi';
import { formatPhone } from '../lib/format';
import AllLinesStrip from '../components/AllLinesStrip';
import CallDetailDrawer from '../components/calls/CallDetailDrawer';
import CallList from '../components/calls/CallList';
import { CALLS_MAX_PAGE, CALLS_PAGE_SIZE, CALLS_POLL_MS, STATUS_META, mergeCall, sortCalls } from '../components/calls/callMeta';
import { useVisiblePoll } from '../components/calls/useVisiblePoll';

/** The status chips, in the order a reviewer scans for trouble. */
const STATUS_FILTERS: OncallCallStatus[] = ['missed', 'voicemail', 'abandoned', 'answered'];

type ReviewFilter = 'all' | OncallReviewStatus;

const REVIEW_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'new', label: 'Needs review' },
  { value: 'reviewed', label: 'Reviewed' },
];

interface ListState {
  /** The site and filters the rows belong to. */
  key: string;
  items: OncallCallSummary[];
  hasMore: boolean;
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
}

const EMPTY_LIST: ListState = { key: '', items: [], hasMore: false, loading: true, loadingMore: false, error: null };

export default function CallsTab() {
  const { currentSite, isSuperAdmin } = useAuth();
  const { callId } = useParams<{ callId?: string }>();
  const navigate = useNavigate();
  const siteId = currentSite?.id ?? null;
  const timeZone = currentSite?.timezone ?? null;

  const [statuses, setStatuses] = useState<OncallCallStatus[]>([]);
  const [review, setReview] = useState<ReviewFilter>('all');
  const [list, setList] = useState<ListState>(EMPTY_LIST);
  const [health, setHealth] = useState<{ siteId: string; data: OncallHealth } | null>(null);
  const [pollTick, setPollTick] = useState(0);

  const listKey = `${siteId ?? ''}|${statuses.join(',')}|${review}`;
  const keyRef = useRef(listKey);
  keyRef.current = listKey;
  const requestRef = useRef(0);
  const loadedRef = useRef(0);
  loadedRef.current = list.key === listKey ? list.items.length : 0;

  const filters = useMemo(
    () => ({
      status: statuses.length ? statuses : undefined,
      review_status: review === 'all' ? undefined : review,
    }),
    [statuses, review],
  );

  /**
   * Read the first page again. `keep` is how many rows are showing (a poll
   * or a refresh re-reads as many as are loaded, up to the server's cap, so
   * "Load more" is not undone every minute).
   */
  const loadFirst = useCallback(
    (mode: 'initial' | 'refresh') => {
      if (!siteId) return;
      const key = listKey;
      const request = ++requestRef.current;
      const limit = mode === 'initial' ? CALLS_PAGE_SIZE : Math.min(CALLS_MAX_PAGE, Math.max(CALLS_PAGE_SIZE, loadedRef.current));
      if (mode === 'initial') setList({ ...EMPTY_LIST, key });
      listCalls({ ...filters, limit, offset: 0 })
        .then((page) => {
          if (request !== requestRef.current || key !== keyRef.current) return;
          setList({
            key,
            items: sortCalls(page?.items ?? []),
            hasMore: page?.page?.hasMore === true,
            loading: false,
            loadingMore: false,
            error: null,
          });
        })
        .catch((err) => {
          if (request !== requestRef.current || key !== keyRef.current) return;
          const message = apiErrorMessage(err, 'The calls could not be loaded');
          // A failed poll keeps the rows it has and says so; a failed first load shows the error alone.
          setList((prev) => ({ ...(prev.key === key ? prev : { ...EMPTY_LIST, key }), loading: false, loadingMore: false, error: message }));
        });
    },
    [siteId, listKey, filters],
  );

  // A new site or new filters: start again from the first page.
  useEffect(() => {
    loadFirst('initial');
  }, [loadFirst]);

  // The line itself (its name and number, and whether the site has one) from /health, once per site.
  useEffect(() => {
    if (!siteId) return undefined;
    let cancelled = false;
    getOncallHealth()
      .then((data) => {
        if (!cancelled) setHealth({ siteId, data });
      })
      .catch(() => {
        // Only wording depends on it: the list still works without it.
      });
    return () => {
      cancelled = true;
    };
  }, [siteId]);

  const refresh = useCallback(() => {
    loadFirst('refresh');
    setPollTick((n) => n + 1);
  }, [loadFirst]);

  useVisiblePoll(refresh, CALLS_POLL_MS, !!siteId);

  const loadMore = () => {
    if (!siteId || list.loadingMore || !list.hasMore) return;
    const key = listKey;
    const request = ++requestRef.current;
    const offset = list.items.length;
    setList((prev) => ({ ...prev, loadingMore: true }));
    listCalls({ ...filters, limit: CALLS_PAGE_SIZE, offset })
      .then((page) => {
        if (request !== requestRef.current || key !== keyRef.current) return;
        setList((prev) => {
          const seen = new Set(prev.items.map((c) => c.id));
          const next = (page?.items ?? []).filter((c) => !seen.has(c.id));
          return { ...prev, items: sortCalls([...prev.items, ...next]), hasMore: page?.page?.hasMore === true, loadingMore: false, error: null };
        });
      })
      .catch((err) => {
        if (request !== requestRef.current || key !== keyRef.current) return;
        setList((prev) => ({ ...prev, loadingMore: false, error: apiErrorMessage(err, 'More calls could not be loaded') }));
      });
  };

  const toggleStatus = (status: OncallCallStatus) => {
    setStatuses((prev) => (prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]));
  };

  const closeDrawer = useCallback(() => navigate('/oncall'), [navigate]);

  const onCallChanged = useCallback(
    (changed: OncallCallSummary) => {
      setList((prev) => {
        const merged = mergeCall(prev.items, changed);
        // A row that no longer matches the review filter leaves the list.
        const kept = review === 'all' ? merged : merged.filter((c) => c.review_status === review);
        return { ...prev, items: sortCalls(kept) };
      });
    },
    [review],
  );

  const siteHealth = health && health.siteId === siteId ? health.data : null;
  const line = siteHealth?.line ?? null;
  const showingList = list.key === listKey;
  const filtered = statuses.length > 0 || review !== 'all';

  const subtitle = line
    ? `${line.name} · ${formatPhone(line.twilio_number_e164)}`
    : "Every call to this site's 24-hour line, with its transcript and review.";

  let body;
  if (!showingList || (list.loading && list.items.length === 0)) {
    body = (
      <div className="flex justify-center py-12">
        <Loader2 role="status" aria-label="Loading calls" className="h-5 w-5 animate-spin text-primary-600 dark:text-primary-400" />
      </div>
    );
  } else if (list.items.length === 0 && list.error) {
    body = (
      <div className="flex flex-col items-center gap-3 py-10 text-center" data-testid="oncall-calls-error">
        <p role="alert" className="flex items-center gap-2 text-sm text-red-700 dark:text-red-400">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
          {list.error}
        </p>
        <button type="button" onClick={() => loadFirst('initial')} className={secondaryBtnCls}>
          Try again
        </button>
      </div>
    );
  } else if (list.items.length === 0) {
    body = (
      <div data-testid="oncall-calls-empty">
        {filtered ? (
          <EmptyState icon={PhoneCall} title="No calls match these filters" hint="Clear a filter to see more calls." variant="card" />
        ) : siteHealth && !line ? (
          <EmptyState
            icon={PhoneCall}
            title="This site has no 24-hour line yet"
            hint="A line admin sets it up in Settings with the number bought for the site."
            variant="card"
          />
        ) : (
          <EmptyState icon={PhoneCall} title="No calls yet" hint="Calls to this site's 24-hour line will appear here." variant="card" />
        )}
      </div>
    );
  } else {
    body = (
      <>
        {list.error && (
          <p role="alert" className="flex items-center gap-2 text-sm text-red-700 dark:text-red-400" data-testid="oncall-calls-stale">
            <AlertTriangle className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
            {list.error}. The list below may be out of date.
          </p>
        )}
        <CallList calls={list.items} timeZone={timeZone} selectedId={callId ?? null} />
        {list.hasMore && (
          <div className="flex justify-center">
            <button
              type="button"
              onClick={loadMore}
              disabled={list.loadingMore}
              className={`${secondaryBtnCls} disabled:cursor-not-allowed disabled:opacity-50`}
              data-testid="oncall-calls-more"
            >
              {list.loadingMore && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
              Load more
            </button>
          </div>
        )}
      </>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[1700px] space-y-4 px-4 pt-4 pb-6 sm:px-6 lg:px-8 lg:pt-5" data-testid="oncall-calls">
      <PageHeader
        title="Calls"
        icon={PhoneCall}
        subtitle={subtitle}
        actions={<RefreshButton onClick={refresh} loading={showingList && list.loading} label="Refresh calls" />}
      />

      {isSuperAdmin && <AllLinesStrip refreshKey={pollTick} currentSiteId={siteId} />}

      <div className="flex flex-wrap items-center gap-2" data-testid="oncall-calls-filters">
        <SegmentedControl
          label="Review state"
          options={REVIEW_OPTIONS}
          value={review}
          onChange={(v) => setReview(v as ReviewFilter)}
        />
        <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Status">
          {STATUS_FILTERS.map((status) => (
            <FilterChip key={status} active={statuses.includes(status)} onClick={() => toggleStatus(status)} title={STATUS_META[status].hint}>
              {STATUS_META[status].label}
            </FilterChip>
          ))}
        </div>
      </div>

      {body}

      <CallDetailDrawer
        callId={callId ?? null}
        siteId={siteId}
        siteName={currentSite?.name}
        timeZone={timeZone}
        onClose={closeDrawer}
        onCallChanged={onCallChanged}
        onCallbackPlaced={refresh}
      />
    </div>
  );
}
